'use client';

import { useCallback } from 'react';

import { IS_TRACKING_ENABLED } from '@/common/constants';
import { useConfig } from '@/components/providers/config-provider';
import { useSafePostHog } from '@/hooks/use-safe-posthog';

interface ForwardEventParams {
  eventName: string;
  properties?: Record<string, unknown>;
}

/**
 * Forward whitelisted analytics events to third-party providers (e.g. GA4)
 * via the `/api/beacon` route. The server reads the configured providers
 * from the tenant config and dispatches accordingly.
 *
 * The current posthog `distinct_id` is attached to every beacon so GA4's
 * `client_id` aligns with PostHog identity and parent embed beacons —
 * without it, the server falls back to a cookie-derived id that diverges
 * under third-party-cookie restrictions.
 *
 * @returns An object exposing `forwardEvent({ eventName, properties })`.
 *   No-ops when tracking is disabled or the event has no rule in
 *   `analyticsForwarding.events`.
 */
export function useAnalyticsForwarding() {
  const { analyticsForwarding } = useConfig();
  const posthog = useSafePostHog();

  const forwardEvent = useCallback(
    ({ eventName, properties }: ForwardEventParams) => {
      if (!IS_TRACKING_ENABLED || !analyticsForwarding) return;

      if (!analyticsForwarding.events[eventName]) return;

      // Send the iframe's posthog distinct_id explicitly so GA4 client_id
      // aligns with PostHog distinct_id and the parent embed beacons.
      // Without this, the server falls back to cookie-derived identity,
      // which is third-party on bazak.ai and divergent under ITP.
      const distinctId = posthog?.get_distinct_id?.() || undefined;

      navigator.sendBeacon?.(
        '/api/beacon',
        JSON.stringify({
          event: eventName,
          properties,
          forwardOnly: true,
          ...(distinctId && { distinctId }),
        }),
      );
    },
    [analyticsForwarding, posthog],
  );

  return { forwardEvent };
}
